/**
 * GateModal — Phase 3 (Drive-First Pivot).
 *
 * Shown when an action (upload/download/view) is blocked by the gate layer.
 * Renders the message + CTA returned by the backend GateResult.
 *
 * Usage:
 *     <GateModal
 *       open={!!gateError}
 *       reason={gateError.reason}
 *       message={gateError.message}
 *       hintAction={gateError.hint_action}
 *       ctaUrl={gateError.cta_url}
 *       severity={gateError.severity}
 *       onClose={() => setGateError(null)}
 *     />
 */

import { useNavigate } from 'react-router-dom'
import {
  HiOutlineCloud,
  HiOutlineCreditCard,
  HiOutlineXMark,
  HiOutlineExclamationTriangle,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'

const REASON_TITLES = {
  no_subscription: 'Connect Google Drive',
  trial_expired: 'Your free trial has ended',
  past_due_grace: 'Payment update needed',
  past_due_expired: 'Subscription needs renewal',
  blocked: 'Subscription required',
  drive_disconnected: 'Drive not connected',
  drive_revoked: 'OVACS lost Drive access',
  drive_quota_full: 'Your Drive is full',
  workspace_owner_no_subscription: 'Workspace owner needs to subscribe',
  workspace_owner_blocked: 'Workspace owner needs to renew',
  cancelled: 'Subscription cancelled',
  drive_disconnected_paid: 'Reconnect Google Drive',
  unknown_state: 'Subscription issue',
}

const ACTION_LABELS = {
  connect_drive: 'Connect Drive',
  reconnect_drive: 'Reconnect Drive',
  subscribe: 'Subscribe now',
  open_drive: 'Open Google Drive',
}

export default function GateModal({
  open,
  reason = '',
  message = '',
  hintAction = null,
  ctaUrl = null,
  severity = 'error',
  onClose,
}) {
  const navigate = useNavigate()
  if (!open) return null

  const title = REASON_TITLES[reason] || 'Action not allowed'
  const ctaLabel = ACTION_LABELS[hintAction] || 'Got it'

  const palette = {
    error:   { bg: 'bg-red-50',    border: 'border-red-300',    icon: 'text-red-600',    btn: 'bg-red-600 hover:bg-red-700' },
    warning: { bg: 'bg-amber-50',  border: 'border-amber-300',  icon: 'text-amber-600',  btn: 'bg-amber-600 hover:bg-amber-700' },
    info:    { bg: 'bg-blue-50',   border: 'border-blue-300',   icon: 'text-blue-600',   btn: 'bg-blue-600 hover:bg-blue-700' },
  }[severity] || {
    bg: 'bg-gray-50', border: 'border-gray-300', icon: 'text-gray-600', btn: 'bg-gray-600 hover:bg-gray-700',
  }

  const Icon =
    severity === 'error' ? HiOutlineExclamationTriangle :
    severity === 'warning' ? HiOutlineExclamationTriangle :
    HiOutlineInformationCircle

  const ActionIcon = hintAction?.startsWith('subscribe') ? HiOutlineCreditCard : HiOutlineCloud

  const handleCta = () => {
    if (ctaUrl) {
      navigate(ctaUrl)
      onClose?.()
      return
    }
    onClose?.()
  }

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="flex justify-end px-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <div className="px-7 pb-7 -mt-2 text-center">
          <div className={`inline-flex w-14 h-14 rounded-full ${palette.bg} border ${palette.border} items-center justify-center mb-4`}>
            <Icon className={`w-7 h-7 ${palette.icon}`} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">{message}</p>

          <div className="flex flex-col gap-2">
            {hintAction && (
              <button
                type="button"
                onClick={handleCta}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white rounded-lg ${palette.btn} transition-colors`}
              >
                <ActionIcon className="w-5 h-5" />
                {ctaLabel}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700 py-2"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
